import { useState, useCallback, useEffect } from 'react';
import {
  analyzeKoreanName,
  generateGlobalNames
} from '../../utils/nameGenerator';
import { flagImageMap } from '../../mocks/nameData';
import { Link, useNavigate } from 'react-router-dom';

interface NameResult {
  country: string;
  flag: string;
  name: string;
  pronunciation: string;
  description: string;
}

export default function Home() {
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [inputName, setInputName] = useState('');
  const [results, setResults] = useState<NameResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-home';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${import.meta.env.VITE_SITE_URL ?? ''}/#website`,
          url: import.meta.env.VITE_SITE_URL ?? '',
          name: '글로벌 이름 테스트',
          description: '한국 이름을 세계 10개국 스타일로 변환해주는 재미 테스트 서비스',
          inLanguage: 'ko',
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${import.meta.env.VITE_SITE_URL ?? ''}/?q={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'WebApplication',
          '@id': `${import.meta.env.VITE_SITE_URL ?? ''}/#app`,
          name: '글로벌 이름 테스트',
          url: import.meta.env.VITE_SITE_URL ?? '',
          description: '한국 이름을 입력하면 일본·중국·영어·스페인·프랑스·독일 등 세계 10개국에서 어떻게 불릴지 알아보는 글로벌 이름 테스트',
          applicationCategory: 'EntertainmentApplication',
          operatingSystem: 'All',
          inLanguage: 'ko',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'KRW',
          },
        },
      ],
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById('schema-home')?.remove();
    };
  }, []);

  const revealCards = useCallback(() => {
    setVisibleCount(0);
    setIsGenerating(true);
    let count = 0;
    const interval = setInterval(() => {
      count += 1;
      setVisibleCount(count);
      if (count >= 10) {
        clearInterval(interval);
        setIsGenerating(false);
      }
    }, 600);
    return () => clearInterval(interval);
  }, []);

  const validateKoreanName = (name: string): boolean => {
    if (!name.trim()) {
      setErrorMessage('이름을 입력해주세요.');
      return false;
    }
    const koreanOnly = /^[가-힣]+$/;
    if (!koreanOnly.test(name.trim())) {
      setErrorMessage('제대로 한글 이름을 입력해주세요.');
      return false;
    }
    if (name.trim().length < 2) {
      setErrorMessage('이름은 2글자 이상 입력해주세요.');
      return false;
    }
    if (name.trim().length > 5) {
      setErrorMessage('이름은 5글자 이하로 입력해주세요.');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const generateNames = () => {
    if (isGenerating) return;
    if (!validateKoreanName(inputName)) return;

    // 한국 이름 분석
    const analysis = analyzeKoreanName(inputName.trim());
    
    // 글로벌 이름 생성
    const newResults = generateGlobalNames(inputName.trim(), gender, analysis);

    setResults(newResults);
    setShowResults(true);
    revealCards();

    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const getShareText = () => {
    if (results.length === 0) return '';
    const nameList = results.map(r => `${r.flag} ${r.country}: ${r.name}`).join('\n');
    return `\ud83c\udf0d \ub0b4 \uc774\ub984, \ud574\uc678\uc5d0\uc120 \uc774\ub807\uac8c \ubd88\ub9b0\ub2e4!\n\n${nameList}\n\n\uc7ac\ubbf8\ub85c \ubcf4\ub294 \uae00\ub85c\ubc8c \uc774\ub984 \ud14c\uc2a4\ud2b8`;
  };

  const shareToKakao = () => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile && navigator.share) {
      navigator.share({
        title: '🌍 내 이름, 해외에선 이렇게 불린다!',
        text: getShareText(),
        url: window.location.href,
      }).catch(() => {});
    } else {
      // PC fallback: 링크 복사
      navigator.clipboard.writeText(getShareText() + '\n\n' + window.location.href).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      });
    }
  };

  const shareToInstagram = () => {
    navigator.clipboard.writeText(getShareText() + '\n\n' + window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    });
  };

  const shareToThreads = () => {
    const text = encodeURIComponent(getShareText());
    window.open(`https://www.threads.net/intent/post?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const copyLink = () => {
    const text = getShareText() + '\n\n' + window.location.href;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const goToCompatibility = () => {
    navigate('/compatibility');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Tab Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex">
            <button
              onClick={() => navigate('/')}
              className="flex-1 py-4 text-sm font-semibold text-center transition-all cursor-pointer border-b-2 border-purple-500 text-purple-600 whitespace-nowrap"
            >
              🌍 글로벌 이름
            </button>
            <button
              onClick={() => navigate('/compatibility')}
              className="flex-1 py-4 text-sm font-semibold text-center transition-all cursor-pointer border-b-2 border-transparent text-gray-400 hover:text-pink-500 whitespace-nowrap"
            >
              💕 이름 궁합
            </button>
          </div>
        </div>
      </nav>

      {/* Header Section */}
      <section className="pt-10 pb-6 px-4 md:pt-20 md:pb-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-3 md:mb-6 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
            내 이름, 해외에선 이렇게 불린다
          </h1>
          <p className="text-lg md:text-2xl text-gray-600">
            재미로 보는 글로벌 이름 테스트
          </p>
        </div>
      </section>

      {/* Input Section */}
      <section className="py-4 px-4 md:py-12">
        <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-xl p-6 md:p-12">
          <div className="space-y-8">
            {/* Gender Selection */}
            <div>
              <label className="block text-lg font-semibold text-gray-700 mb-4">
                성별 선택
              </label>
              <div className="flex gap-4">
                <button
                  onClick={() => setGender('male')}
                  className={`flex-1 py-4 px-6 rounded-xl text-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                    gender === 'male'
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  남
                </button>
                <button
                  onClick={() => setGender('female')}
                  className={`flex-1 py-4 px-6 rounded-xl text-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                    gender === 'female'
                      ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  여
                </button>
              </div>
            </div>

            {/* Name Input */}
            <div>
              <label className="block text-lg font-semibold text-gray-700 mb-4">
                이름 입력
              </label>
              <input
                type="text"
                value={inputName}
                onChange={(e) => {
                  setInputName(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                placeholder="예) 김민수"
                className={`w-full py-4 px-6 text-lg border-2 rounded-xl focus:outline-none transition-colors ${
                  errorMessage
                    ? 'border-red-400 focus:border-red-500'
                    : 'border-gray-200 focus:border-purple-500'
                }`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    generateNames();
                  }
                }}
              />
              {errorMessage && (
                <div className="flex items-center gap-2 mt-3 text-red-500">
                  <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm font-medium">{errorMessage}</span>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              onClick={generateNames}
              disabled={isGenerating}
              className={`relative w-full py-5 px-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xl font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer overflow-hidden ${isGenerating ? 'opacity-60 cursor-not-allowed hover:scale-100' : ''}`}
            >
              <span className="relative z-10">
                {isGenerating ? '번역 중...' : '글로벌 이름 짓기'}
              </span>
              {!isGenerating && (
                <span className="absolute inset-0 z-0 shimmer-light" />
              )}
            </button>

            {/* 위치2: 버튼 아래 광고 배너 */}
            <a
              href="https://bookk.co.kr/bookStore/68d3f712ac751d87d0a8489c"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full cursor-pointer"
            >
              <img
                src="https://storage.readdy-site.link/project_files/a54e172b-202b-43f9-932c-f9b2f2c2da78/8897fae7-6e71-4515-b662-5f6edbfb0f06_Untitled-1.jpg?v=667f8f7ac8e9a6eb98144c1d58b57c25"
                alt="광고 배너"
                className="w-full rounded-xl object-cover"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Compatibility Banner */}
      <section className="py-3 px-4 md:py-5">
        <div className="max-w-2xl mx-auto">
          <div
            onClick={goToCompatibility}
            className="block bg-gradient-to-r from-pink-500 to-rose-400 rounded-xl shadow-md p-4 hover:shadow-lg hover:scale-[1.01] transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm font-medium mb-0.5">NEW ✨</p>
                <p className="text-white text-lg font-bold mb-0.5">이름 궁합률 테스트</p>
                <p className="text-white/80 text-sm">두 이름의 궁합 점수를 확인해보세요 💕</p>
              </div>
              <div className="text-4xl group-hover:scale-110 transition-transform">💘</div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section - Sequential Flip Cards */}
      {showResults && (
        <section id="results" className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
              {results.map((result, index) => {
                const isVisible = index < visibleCount;
                const isCurrentlyFlipping = index === visibleCount - 1;
                return (
                  <div
                    key={`${result.country}-${index}`}
                    className="perspective-1000"
                    style={{
                      perspective: '1200px',
                      opacity: isVisible ? 1 : 0,
                      visibility: isVisible ? 'visible' : 'hidden',
                      transition: 'opacity 0.3s ease',
                    }}
                  >
                    <div
                      className="relative w-full"
                      style={{
                        transformStyle: 'preserve-3d',
                        animation: isCurrentlyFlipping ? 'cardFlipIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' : 'none',
                        transform: isVisible && !isCurrentlyFlipping ? 'rotateX(0deg)' : undefined,
                      }}
                    >
                      <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                        {/* Flag Image Banner */}
                        <div className="w-full h-36 relative overflow-hidden">
                          <img
                            src={flagImageMap[result.country]}
                            alt={`${result.country} 국기`}
                            className="w-full h-full object-cover object-top"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                          <div className="absolute bottom-3 left-5 flex items-center gap-2">
                            <span className="text-4xl drop-shadow-lg">{result.flag}</span>
                            <span className="text-xl font-bold text-white drop-shadow-lg">{result.country}</span>
                          </div>
                        </div>
                        {/* Name & Description */}
                        <div className="p-4 md:p-6 text-center">
                          <p className="text-xl md:text-3xl font-bold text-gray-900 mb-1 break-words leading-tight min-w-0 overflow-hidden">
                            {result.name}
                          </p>
                          <p className="text-sm md:text-base text-purple-500 font-medium mb-2 whitespace-nowrap overflow-hidden text-ellipsis">
                            [ {result.pronunciation} ]
                          </p>
                          <p className="text-xs md:text-sm text-gray-600 leading-relaxed break-words min-w-0">
                            {result.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Loading indicator while generating */}
            {isGenerating && visibleCount < 10 && (
              <div className="flex justify-center mt-8">
                <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-full py-3 px-6 shadow-md">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2.5 h-2.5 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2.5 h-2.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">다음 나라 번역 중...</span>
                </div>
              </div>
            )}

            {/* Share Buttons - show after all cards revealed */}
            {visibleCount >= 10 && (
              <div className="mt-12 animate-fadeIn" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
                <div className="bg-white rounded-2xl shadow-lg p-8 max-w-xl mx-auto">
                  <p className="text-center text-lg font-semibold text-gray-700 mb-6">
                    친구에게 공유하기
                  </p>
                  <div className="flex justify-center gap-4">
                    <button
                      onClick={shareToKakao}
                      className="flex flex-col items-center gap-2 cursor-pointer group"
                    >
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-yellow-400 hover:bg-yellow-500 transition-all group-hover:scale-110 shadow-md">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M12 3C6.48 3 2 6.58 2 10.94c0 2.8 1.86 5.27 4.66 6.67-.15.56-.96 3.6-.99 3.83 0 0-.02.17.09.23.11.07.24.02.24.02.31-.04 3.65-2.4 4.22-2.81.58.08 1.17.12 1.78.12 5.52 0 10-3.58 10-7.94S17.52 3 12 3z" fill="#3C1E1E"/>
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600 font-medium">카카오</span>
                    </button>

                    <button
                      onClick={shareToInstagram}
                      className="flex flex-col items-center gap-2 cursor-pointer group relative"
                    >
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 hover:from-yellow-500 hover:via-pink-600 hover:to-purple-700 transition-all group-hover:scale-110 shadow-md">
                        <i className="ri-instagram-line text-xl text-white"></i>
                      </div>
                      <span className="text-xs text-gray-600 font-medium">인스타</span>
                      {copied && (
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs py-1.5 px-3 rounded-lg whitespace-nowrap animate-fadeIn">
                          텍스트 복사됨!
                        </div>
                      )}
                    </button>

                    <button
                      onClick={shareToThreads}
                      className="flex flex-col items-center gap-2 cursor-pointer group"
                    >
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-900 hover:bg-gray-800 transition-all group-hover:scale-110 shadow-md">
                        <i className="ri-threads-line text-xl text-white"></i>
                      </div>
                      <span className="text-xs text-gray-600 font-medium">스레드</span>
                    </button>

                    <button
                      onClick={copyLink}
                      className="flex flex-col items-center gap-2 cursor-pointer group relative"
                    >
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-all group-hover:scale-110 shadow-md">
                        <i className="ri-link text-xl text-gray-700"></i>
                      </div>
                      <span className="text-xs text-gray-600 font-medium">링크복사</span>
                      {copied && (
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs py-1.5 px-3 rounded-lg whitespace-nowrap animate-fadeIn">
                          복사 완료!
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                {/* 위치1: 공유 버튼 아래 광고 배너 */}
                <div className="max-w-xl mx-auto mt-5">
                  <a
                    href="https://bookk.co.kr/bookStore/68d3f712ac751d87d0a8489c"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full cursor-pointer"
                  >
                    <img
                      src="https://storage.readdy-site.link/project_files/a54e172b-202b-43f9-932c-f9b2f2c2da78/8897fae7-6e71-4515-b662-5f6edbfb0f06_Untitled-1.jpg?v=667f8f7ac8e9a6eb98144c1d58b57c25"
                      alt="광고 배너"
                      className="w-full rounded-2xl object-cover"
                    />
                  </a>
                </div>

                {/* Compatibility Banner after results */}
                <div className="max-w-xl mx-auto mt-5">
                  <div
                    onClick={goToCompatibility}
                    className="block bg-gradient-to-r from-pink-500 to-rose-400 rounded-xl shadow-md p-4 hover:shadow-lg hover:scale-[1.01] transition-all cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-sm font-medium mb-0.5">NEW ✨</p>
                        <p className="text-white text-lg font-bold mb-0.5">이름 궁합률 테스트</p>
                        <p className="text-white/80 text-sm">두 이름의 궁합 점수를 확인해보세요 💕</p>
                      </div>
                      <div className="text-4xl group-hover:scale-110 transition-transform">💘</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Explanation Section */}
      <section className="py-3 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white/70 border border-pink-100 rounded-2xl px-5 py-4 text-center">
            <p className="text-lg text-gray-900 leading-relaxed">
              재미로 즐기는 글로벌 이름 테스트예요! 생성된 이름은 실제 언어·문화와 다를 수 있으니 가볍게 즐겨주세요. 😊
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-4 px-4 bg-pink-50 border-t border-pink-100">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-4">
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/about" className="hover:text-purple-600 transition-colors cursor-pointer">About</Link>
            <span className="text-gray-300">|</span>
            <Link to="/contact" className="hover:text-purple-600 transition-colors cursor-pointer">Contact</Link>
            <span className="text-gray-300">|</span>
            <Link to="/privacy" className="hover:text-purple-600 transition-colors cursor-pointer">Privacy Policy</Link>
          </div>
          <p className="text-xs text-gray-400">© 2026 나홀로 사장. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
        @keyframes cardFlipIn {
          0% {
            transform: rotateX(-90deg) scale(0.9);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: rotateX(0deg) scale(1);
            opacity: 1;
          }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-15deg); }
          100% { transform: translateX(250%) skewX(-15deg); }
        }
        .shimmer-light {
          display: block;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
          animation: shimmer 2.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
